import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, OverlayTrigger, Tooltip, ProgressBar, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate, formatDateTime } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function MouvementsStock() {
    const navigate = useNavigate();
    const [mouvements, setMouvements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [stats, setStats] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [sortConfig, setSortConfig] = useState({ key: 'date_mouvement', direction: 'desc' });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchMouvements();
        fetchStats();
    }, [filterType, dateDebut, dateFin]);

    const fetchMouvements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/stock_etat.php?action=mouvements`;
            
            const params = new URLSearchParams();
            if (filterType) params.append('type_mouvement', filterType);
            if (dateDebut) params.append('date_debut', dateDebut);
            if (dateFin) params.append('date_fin', dateFin);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setMouvements(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getTypeBadge = (type) => {
        const configs = {
            entree: { color: '#10b981', icon: 'ri-arrow-down-line', label: 'Entrée' },
            sortie: { color: '#ef4444', icon: 'ri-arrow-up-line', label: 'Sortie' },
            inventaire: { color: '#0ea5e9', icon: 'ri-survey-line', label: 'Inventaire' },
            reservation: { color: '#f59e0b', icon: 'ri-lock-line', label: 'Réservation' },
            annulation: { color: '#64748b', icon: 'ri-close-line', label: 'Annulation' }
        };
        const config = configs[type] || configs.sortie;
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredMouvements = mouvements
        .filter(m => 
            m.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
            m.designation?.toLowerCase().includes(filterText.toLowerCase())
        )
        .sort((a, b) => {
            if (sortConfig.key === 'date_mouvement') {
                const dateA = new Date(a.date_mouvement);
                const dateB = new Date(b.date_mouvement);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            if (sortConfig.key === 'quantite') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite || 0) - (b.quantite || 0)
                    : (b.quantite || 0) - (a.quantite || 0);
            }
            return 0;
        });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMouvements.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredMouvements.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalEntrees = mouvements
        .filter(m => m.type_mouvement === 'entree')
        .reduce((sum, m) => sum + (m.quantite || 0), 0);
    
    const totalSorties = mouvements
        .filter(m => m.type_mouvement === 'sortie')
        .reduce((sum, m) => sum + (m.quantite || 0), 0);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des mouvements...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchMouvements}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-history-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Mouvements de stock</h4>
                                    <p className="text-muted small mb-0">
                                        {filteredMouvements.length} mouvements
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                className="rounded-pill px-3"
                                style={viewMode === 'table' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-table-line me-1"></i>Tableau
                            </Button>
                            <Button 
                                variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('cards')}
                                className="rounded-pill px-3"
                                style={viewMode === 'cards' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-grid-line me-1"></i>Cartes
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/etat/instantane')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-stack-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total mouvements</span>
                                    <h3 className="kpi-value">{filteredMouvements.length}</h3>
                                    <span className="kpi-unit">tous types confondus</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-arrow-down-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total entrées</span>
                                    <h3 className="kpi-value">{totalEntrees}</h3>
                                    <span className="kpi-unit">articles reçus</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon">
                                    <i className="ri-arrow-up-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total sorties</span>
                                    <h3 className="kpi-value">{totalSorties}</h3>
                                    <span className="kpi-unit">articles sortis</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres avancés modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={4}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <Form.Select 
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="">Tous types</option>
                                        <option value="entree">Entrées</option>
                                        <option value="sortie">Sorties</option>
                                        <option value="inventaire">Inventaires</option>
                                        <option value="reservation">Réservations</option>
                                        <option value="annulation">Annulations</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        type="date"
                                        value={dateDebut}
                                        onChange={(e) => setDateDebut(e.target.value)}
                                        className="rounded-pill"
                                        placeholder="Date début"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        type="date"
                                        value={dateFin}
                                        onChange={(e) => setDateFin(e.target.value)}
                                        className="rounded-pill"
                                        placeholder="Date fin"
                                    />
                                </Col>
                                <Col md={2}>
                                    <div className="d-flex gap-2">
                                        <Form.Select 
                                            value={sortConfig.key}
                                            onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                            className="rounded-pill"
                                        >
                                            <option value="date_mouvement">Trier par date</option>
                                            <option value="quantite">Trier par quantité</option>
                                        </Form.Select>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setFilterText('');
                                                setFilterType('');
                                                setDateDebut('');
                                                setDateFin('');
                                            }} 
                                            className="rounded-pill d-flex align-items-center justify-content-center"
                                            style={{ width: '42px' }}
                                        >
                                            <i className="ri-refresh-line"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <SortableHeader field="date_mouvement">Date</SortableHeader>
                                                <th>Type</th>
                                                <th>Produit</th>
                                                <th>Réf.</th>
                                                <SortableHeader field="quantite" className="text-center">Quantité</SortableHeader>
                                                <th className="text-center">Stock avant</th>
                                                <th className="text-center">Stock après</th>
                                                <th>Document</th>
                                                <th>Utilisateur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.length > 0 ? (
                                                currentItems.map((m) => (
                                                    <tr key={m.id_mouvement} className="mouvement-row">
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-semibold">{formatDate(m.date_mouvement)}</span>
                                                                <small className="text-muted">{formatDateTime(m.date_mouvement).split(' ')[1]}</small>
                                                            </div>
                                                        </td>
                                                        <td>{getTypeBadge(m.type_mouvement)}</td>
                                                        <td>
                                                            <Link to={`/stock/produits/${m.id_produit}`} className="table-link">
                                                                {m.designation}
                                                            </Link>
                                                       </td>
                                                        <td>
                                                            <span className="reference-badge">{m.reference}</span>
                                                     </td>
                                                        <td className="text-center">
                                                            <span className={`fw-bold fs-5 ${
                                                                m.type_mouvement === 'entree' ? 'text-success' : 
                                                                m.type_mouvement === 'sortie' ? 'text-danger' : 'text-info'
                                                            }`}>
                                                                {m.type_mouvement === 'entree' ? '+' : 
                                                                 m.type_mouvement === 'sortie' ? '-' : ''}{m.quantite}
                                                      
                                                        </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge className="rounded-pill px-3" style={{ background: '#64748b' }}>{m.stock_avant}</Badge>
                                                      </td>
                                                        <td className="text-center">
                                                            <Badge className="rounded-pill px-3" style={{ background: '#21ACCD' }}>{m.stock_apres}</Badge>
</td>
                                                        <td>
                                                            {m.reference_document ? (
                                                                <div className="d-flex flex-column">
                                                                    <Badge className="rounded-pill px-2 mb-1" style={{ background: '#0ea5e9' }}>
                                                                        {m.type_document}
                                                                    </Badge>
                                                                    <small className="text-muted">{m.reference_document}</small>
                                                                </div>
                                                            ) : <span className="text-muted">-</span>}
                                                      </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <i className="ri-user-line text-muted"></i>
                                                                <span className="small">{m.prenom_utilisateur} {m.nom_utilisateur}</span>
                                                            </div>
                                                       </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="ri-history-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                            <h5 className="mt-3">Aucun mouvement trouvé</h5>
                                                            <p className="text-muted mb-3">
                                                                {filterText || filterType || dateDebut || dateFin 
                                                                    ? "Aucun résultat ne correspond à vos filtres"
                                                                    : "Aucun mouvement de stock enregistré"}
                                                            </p>
                                                            {(filterText || filterType || dateDebut || dateFin) && (
                                                                <Button 
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        setFilterText('');
                                                                        setFilterType('');
                                                                        setDateDebut('');
                                                                        setDateFin('');
                                                                    }}
                                                                    className="rounded-pill"
                                                                >
                                                                    <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                                </Button>
                                                            )}
                                                        </div>
                                                </td>
                                            </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                        <div className="text-muted small">
                                            <i className="ri-file-list-line me-1"></i>
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredMouvements.length)} sur {filteredMouvements.length} mouvements
                                        </div>
                                        <Pagination className="mb-0">
                                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                            {renderPagination()}
                                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue Cartes */}
                    {viewMode === 'cards' && (
                        <>
                            <Row className="g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map((m) => (
                                        <Col lg={6} xl={4} key={m.id_mouvement}>
                                            <Card className="border-0 shadow-sm rounded-3 h-100 mouvement-card">
                                                <Card.Body className="p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div>
                                                            <span className="status-badge mb-2 d-inline-flex" style={{ background: `${m.type_mouvement === 'entree' ? '#10b981' : m.type_mouvement === 'sortie' ? '#ef4444' : '#0ea5e9'}15`, color: m.type_mouvement === 'entree' ? '#10b981' : m.type_mouvement === 'sortie' ? '#ef4444' : '#0ea5e9' }}>
                                                                <i className={`${m.type_mouvement === 'entree' ? 'ri-arrow-down-line' : m.type_mouvement === 'sortie' ? 'ri-arrow-up-line' : 'ri-survey-line'} me-1 fs-10`}></i>
                                                                {m.type_mouvement === 'entree' ? 'Entrée' : m.type_mouvement === 'sortie' ? 'Sortie' : 'Inventaire'}
                                                            </span>
                                                            <h5 className="mb-1">{m.designation}</h5>
                                                            <small className="text-muted">{m.reference}</small>
                                                        </div>
                                                        <div className="text-end">
                                                            <span className={`fw-bold fs-3 ${
                                                                m.type_mouvement === 'entree' ? 'text-success' : 
                                                                m.type_mouvement === 'sortie' ? 'text-danger' : 'text-info'
                                                            }`}>
                                                                {m.type_mouvement === 'entree' ? '+' : 
                                                                 m.type_mouvement === 'sortie' ? '-' : ''}{m.quantite}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <small className="text-muted">
                                                                <i className="ri-time-line me-1"></i>
                                                                {formatDateTime(m.date_mouvement)}
                                                            </small>
                                                            <small className="text-muted">
                                                                <i className="ri-user-line me-1"></i>
                                                                {m.prenom_utilisateur} {m.nom_utilisateur}
                                                            </small>
                                                        </div>
                                                        
                                                        <div className="bg-light p-3 rounded-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span className="text-muted">Stock avant</span>
                                                                <span className="fw-semibold">{m.stock_avant}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between">
                                                                <span className="text-muted">Stock après</span>
                                                                <span className="fw-semibold text-primary">{m.stock_apres}</span>
                                                            </div>
                                                        </div>

                                                        {m.reference_document && (
                                                            <div className="mt-2 small text-muted">
                                                                <i className="ri-file-text-line me-1"></i>
                                                                {m.type_document}: {m.reference_document}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <hr className="my-3" />

                                                    <div className="d-flex justify-content-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                className="action-btn"
                                                                onClick={() => navigate(`/stock/produits/${m.id_produit}`)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))
                                ) : (
                                    <Col md={12}>
                                        <Card className="border-0 shadow-sm rounded-3 text-center py-5">
                                            <i className="ri-history-line fs-48 text-muted mb-3 d-block"></i>
                                            <h5 className="mt-3">Aucun mouvement trouvé</h5>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* Pagination pour la vue cartes */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredMouvements.length)} sur {filteredMouvements.length} mouvements
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .mouvement-row {
                    transition: all 0.2s ease;
                }
                
                .mouvement-row:hover {
                    background: #f8fafc;
                }
                
                /* Mouvement Card */
                .mouvement-card {
                    transition: all 0.3s ease;
                }
                
                .mouvement-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}